import { lazy, Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';

const DocumentHubPage = lazy(() => import('./pages/DocumentHubPage'));
const DocumentAreaPage = lazy(() => import('./pages/DocumentAreaPage'));
const PageRouter = lazy(() => import('./pages/PageRouter'));
const DocumentSearchPage = lazy(() => import('./pages/DocumentSearchPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const TrashPage = lazy(() => import('./pages/TrashPage'));

const PageSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="flex items-center gap-3 text-neutral-400 animate-pulse">
        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Loading...
      </div>
    </div>
  }>
    {children}
  </Suspense>
);

/**
 * Documentation route children — org-scoped pages.
 * Composed into the parent <Routes> in App.tsx.
 *
 * Area pages (/dc/product, /dc/success, etc.) use a split layout:
 * sidebar on left + document content on right via nested <Outlet>.
 * Selecting a doc navigates to /dc/:area/:docId.
 */
export function DocumentationRouteChildren() {
  const areaRoute = (area: string) => (
    <Route key={area} path={area} element={<PageSuspense><DocumentAreaPage /></PageSuspense>}>
      <Route path=":docId" element={<PageSuspense><PageRouter /></PageSuspense>} />
    </Route>
  );

  return (
    <>
      <Route index element={<PageSuspense><DocumentHubPage /></PageSuspense>} />
      <Route path="search" element={<PageSuspense><DocumentSearchPage /></PageSuspense>} />
      <Route path="templates" element={<PageSuspense><DocumentTemplatesPage /></PageSuspense>} />
      <Route path="trash" element={<PageSuspense><TrashPage /></PageSuspense>} />

      {/* Area views — each with nested :docId for split-panel document display */}
      {areaRoute('product')}
      {areaRoute('success')}
      {areaRoute('sales')}
      {areaRoute('ops')}
      {areaRoute('archie')}

      {/* Standalone document/database page — direct link /dc/:docId (no sidebar) */}
      <Route path=":docId" element={<PageSuspense><PageRouter /></PageSuspense>} />
    </>
  );
}
